<template>
  <div
    v-if="includeSections.installments && paymentType === 'installment'"
    style="
      background-color: var(--color-light-light-gray);
      padding: 20px;
      margin-bottom: 40px;
      border-radius: 4px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    "
  >
    <h4 class="h5 fw-bold mb-3">
      {{ $t("project.previewAndReport.installments.monthlyCoverage.title") }}
    </h4>

    <!-- Installment Data -->
    <div class="row justify-content-center">
      <div class="col-md-6">
        <div class="d-flex justify-content-center">
          <div
            class="d-flex align-items-end"
            style="height: 220px; width: 100%"
          >
            <div class="d-flex justify-content-around w-100">
              <!-- Payment Bar -->
              <div class="d-flex flex-column align-items-center">
                <div
                  style="width: 120px; height: 180px"
                  class="position-relative"
                >
                  <div
                    class="w-100 position-absolute bottom-0"
                    style="background-color: var(--color-red)"
                    :style="{
                      height: getPaymentBarHeight(),
                      borderRadius: '2px 2px 0 0',
                    }"
                  >
                    <div
                      class="text-white small fw-bold d-flex align-items-center justify-content-center"
                      :style="{ height: '100%' }"
                    >
                      {{
                        formatCurrency(
                          projectData?.financial_data?.installment_amount || 0
                        )
                      }}
                    </div>
                  </div>
                </div>
                <div class="fw-bold mt-2">
                  {{
                    $t(
                      "project.previewAndReport.installments.monthlyCoverage.monthlyPayment"
                    )
                  }}
                </div>
                <div class="small" style="font-size: 12px">
                  {{
                    formatCurrency(
                      projectData?.financial_data?.installment_amount || 0
                    )
                  }}
                </div>
              </div>

              <!-- Savings Bar -->
              <div class="d-flex flex-column align-items-center">
                <div
                  style="width: 120px; height: 180px"
                  class="position-relative"
                >
                  <!-- Energy production -->
                  <div
                    class="w-100 position-absolute"
                    style="background-color: var(--color-green)"
                    :style="{
                      bottom: getMonthlyCashbackHeight(),
                      height: getMonthlyEnergyHeight(),
                      borderRadius: '2px 2px 0 0',
                    }"
                  >
                    <div
                      class="text-white small fw-bold d-flex align-items-center justify-content-center"
                      :style="{ height: '100%' }"
                    >
                      {{
                        formatCurrency(
                          (projectData?.financial_data?.annual_revenue || 0) /
                            12
                        )
                      }}
                    </div>
                  </div>

                  <!-- Cashback -->
                  <div
                    v-if="shouldShowCashback()"
                    class="w-100 position-absolute bottom-0"
                    style="background-color: var(--color-graph-green)"
                    :style="{
                      height: getMonthlyCashbackHeight(),
                      borderRadius: '0 0 2px 2px',
                    }"
                  >
                    <div
                      class="text-dark small fw-bold d-flex align-items-center justify-content-center"
                      :style="{ height: '100%' }"
                    >
                      {{
                        formatCurrency(
                          (projectData?.financial_data?.cashback_amount || 0) / 120
                        )
                      }}
                    </div>
                  </div>
                </div>
                <div class="fw-bold mt-2">
                  {{
                    $t(
                      "project.previewAndReport.installments.monthlyCoverage.monthlySaving"
                    )
                  }}
                </div>
                <div class="small" style="font-size: 12px">
                  {{
                    formatCurrency(
                      (projectData?.financial_data?.annual_revenue || 0) / 12 +
                      (shouldShowCashback() ? (projectData?.financial_data?.cashback_amount || 0) / 120 : 0)
                    )
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Legend -->
        <div class="mt-4">
          <div class="d-flex justify-content-center">
            <div
              class="badge me-2"
              style="
                background-color: var(--color-red);
                border-radius: 50%;
                width: 16px;
                height: 16px;
              "
            >
              &nbsp;
            </div>
            <div class="me-4" style="font-size: 12px">
              {{
                $t(
                  "project.previewAndReport.installments.monthlyCoverage.monthlyPayment"
                )
              }}
            </div>
            <div
              v-if="shouldShowCashback()"
              class="badge me-2"
              style="
                background-color: var(--color-graph-green);
                border-radius: 50%;
                width: 16px;
                height: 16px;
              "
            >
              &nbsp;
            </div>
            <div v-if="shouldShowCashback()" class="me-4" style="font-size: 12px">
              {{
                $t(
                  "project.previewAndReport.installments.monthlyCoverage.cashback"
                )
              }}
            </div>
            <div
              class="badge me-2"
              style="
                background-color: var(--color-green);
                border-radius: 50%;
                width: 16px;
                height: 16px;
              "
            >
              &nbsp;
            </div>
            <div style="font-size: 12px">
              {{
                $t(
                  "project.previewAndReport.installments.monthlyCoverage.energyProduction"
                )
              }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cash Flow Description -->
    <div class="mt-3">
      <p class="mb-0" style="font-size: 12px" v-html="$t('project.previewAndReport.cashFlow.description')">
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import type { Project } from "@/types/projectTypes";

export default defineComponent({
  name: "MonthlyCoverageSection",
  props: {
    projectData: {
      type: Object as PropType<Project | null>,
      required: true,
    },
    includeSections: {
      type: Object as PropType<Record<string, boolean>>,
      required: true,
    },
    paymentType: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const formatCurrency = (value: number): string => {
      return new Intl.NumberFormat("en-EU", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    };

    const getMaxValue = (): number => {
      const monthlyPayment =
        props.projectData?.financial_data?.installment_amount || 0;
      const monthlyCashback = shouldShowCashback() ? (props.projectData?.financial_data?.cashback_amount|| 0) / 120 : 0;
      const monthlyEnergy =
        (props.projectData?.financial_data?.annual_revenue || 0) / 12;
      const totalSavings = monthlyCashback + monthlyEnergy;
      return Math.max(monthlyPayment, totalSavings);
    };

    const getPaymentBarHeight = (): string => {
      const monthlyPayment =
        props.projectData?.financial_data?.installment_amount || 0;
      const maxValue = getMaxValue();
      return maxValue > 0 ? (monthlyPayment / maxValue) * 180 + "px" : "0px";
    };

    const getMonthlyCashbackHeight = (): string => {
      const monthlyCashback = (props.projectData?.financial_data?.cashback_amount || 0) / 120;
      const maxValue = getMaxValue();
      return maxValue > 0 ? (monthlyCashback / maxValue) * 180 + "px" : "0px";
    };

    const getMonthlyEnergyHeight = (): string => {
      const monthlyEnergy =
        (props.projectData?.financial_data?.annual_revenue || 0) / 12;
      const maxValue = getMaxValue();
      return maxValue > 0 ? (monthlyEnergy / maxValue) * 180 + "px" : "0px";
    };

    const shouldShowCashback = (): boolean => {
      return (props.projectData?.financial_data?.cashback_percentage || 0) > 0;
    };

    return {
      formatCurrency,
      getPaymentBarHeight,
      getMonthlyCashbackHeight,
      getMonthlyEnergyHeight,
      shouldShowCashback,
    };
  },
});
</script>
